import { useRef } from 'react';
import RadioButton from '@components/DesignLibrary/Atoms/RadioButton';
import usePopover from '@/hooks/usePopover';
import { createPortal } from 'react-dom';

import { m } from 'framer-motion';
import s from '../Filters.module.scss';

type Props<K> = {
  items: Array<{
    key: K;
    label: string;
  }>;
  selected: K;
  label: string;
  onChange: (selected: K) => void;
};

const FilterOne = <K extends string>({ onChange, items, selected, label }: Props<K>): React.ReactElement => {
  const { isOpen, setIsOpen, menuRef, triggerRef } = usePopover('bottom-left');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = (key: K, newValue: boolean): void => {
    if (newValue && key !== selected) {
      onChangeRef.current(key);
    }
  };

  const selectedItem = items.find(item => item.key === selected);

  return (
    <div className={s.container} ref={containerRef}>
      <button ref={triggerRef} type="button" onClick={() => setIsOpen(!isOpen)} style={isOpen ? { backgroundColor: 'var(--color-gray-25)' } : undefined}>
        <span>
          {label}
          {selectedItem && `: ${selectedItem.label}`}
        </span>
      </button>
      {isOpen && createPortal(
        <m.fieldset
          ref={menuRef as unknown as React.RefObject<HTMLFieldSetElement>}
          className={s.inputs}
          initial={{
            opacity: 0,
            y: 8,
          }}
          animate={{
            opacity: 1,
            y: 4,
          }}
        >
          {items.map(i => (
            <RadioButton
              key={i.key}
              size="sm"
              isChecked={Boolean(selectedItem && selectedItem.key === i.key)}
              onChange={value => handleChange(i.key, value)}
              label={i.label}
            />
          ))}
        </m.fieldset>,
        document.body,
      )}
    </div>
  );
};

export default FilterOne;
